/**
 * Get Started widget state store.
 *
 * The widget has three states that the user toggles between:
 *
 *  - `expanded`  — full panel rendered bottom-right (default)
 *  - `collapsed` — header-only bar bottom-right (caret button collapses)
 *  - `minimized` — bottom-right hidden entirely; a restore pill lives in the
 *                  top navigation (X button on the panel triggers this).
 *                  Clicking the top-bar pill returns the user to `expanded`.
 *
 * This mirrors Stripe's Setup guide pattern: collapse keeps it nearby,
 * minimize hides it from the workspace but stays one click away in the top
 * nav so the user never loses their progress thread.
 *
 * Persisted to localStorage so the chosen state survives reloads — closing
 * the widget once shouldn't make it pop back open on every page navigation.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type GetStartedWidgetState = 'expanded' | 'collapsed' | 'minimized';

interface GetStartedWidgetStore {
  /** Current widget state — see file header for the three values' meanings. */
  widgetState: GetStartedWidgetState;
  /**
   * Transient flag set by `restoreFromPill` and cleared by the checklist
   * component after it processes the restore (plays fade-in, resets section
   * state, then auto-expands the next relevant step). Not persisted —
   * always starts false so reloads don't trigger a spurious re-entrance.
   */
  restorationPending: boolean;
  /**
   * Symmetric counterpart to `restorationPending` for the OTHER direction:
   * set by `minimizeToPill` when the user clicks X on the bottom-right
   * panel, cleared by the pill after it plays its slide-in animation. Not
   * persisted, so on a page reload with widgetState already === 'minimized'
   * (restored from localStorage) the pill renders statically with no
   * spurious entrance animation.
   */
  justMinimized: boolean;
  /** Set state directly (used by the bottom-right caret). */
  setWidgetState: (state: GetStartedWidgetState) => void;
  /** Convenience: caret button toggles expanded ↔ collapsed (never touches minimized). */
  toggleCollapsed: () => void;
  /**
   * X-button handler. Sets widgetState='minimized' AND marks justMinimized
   * so the top-bar pill plays its slide-in animation on this fresh mount
   * (vs. silently appearing on a reload that started with stored 'minimized').
   */
  minimizeToPill: () => void;
  /**
   * Pill click handler. Expands the widget AND sets `restorationPending` so
   * the checklist knows to play the entrance animation and re-pick the next
   * relevant step instead of restoring whatever section was last expanded.
   */
  restoreFromPill: () => void;
  /** Checklist clears the flag after it has consumed the restore signal. */
  clearRestoration: () => void;
  /** Pill clears the flag after it has consumed the just-minimized signal. */
  clearJustMinimized: () => void;
  /**
   * True once the user clicks the download link in the "Get your screen set
   * up" checklist step. That step has no backend signal — it completes purely
   * on this client-side action. Persisted so the check survives reloads.
   */
  screenSetupDownloadClicked: boolean;
  /** Marks the download link as clicked — completes the "Get your screen set up" step. */
  markScreenSetupDownloadClicked: () => void;
}

export const useGetStartedWidgetStore = create<GetStartedWidgetStore>()(
  persist(
    (set) => ({
      widgetState: 'expanded',
      restorationPending: false,
      justMinimized: false,
      setWidgetState: (widgetState) => set({ widgetState }),
      toggleCollapsed: () =>
        set((s) => ({
          widgetState: s.widgetState === 'expanded' ? 'collapsed' : 'expanded',
        })),
      minimizeToPill: () => set({ widgetState: 'minimized', justMinimized: true }),
      restoreFromPill: () => set({ widgetState: 'expanded', restorationPending: true }),
      clearRestoration: () => set({ restorationPending: false }),
      clearJustMinimized: () => set({ justMinimized: false }),
      screenSetupDownloadClicked: false,
      markScreenSetupDownloadClicked: () => set({ screenSetupDownloadClicked: true }),
    }),
    {
      name: 'scio3-get-started-widget',
      // Persist widgetState + screenSetupDownloadClicked — restorationPending
      // and justMinimized are transient UI cues that must not survive a reload.
      partialize: (s) => ({
        widgetState: s.widgetState,
        screenSetupDownloadClicked: s.screenSetupDownloadClicked,
      }),
    },
  ),
);
